import {
  Box,
  AppBar as MuiAppBar,
  Drawer,
  Grid,
  Toolbar,
  ListItem,
  styled as muiStyled,
} from '@mui/material';
import { styled } from 'styled-components';
import { DRAWER_WIDTH, COLLAPSED_DRAWER_WIDTH, HEADER_HEIGHT } from './constants';
import { IListMenu, MenuStyleProps } from './types';

/**
 * AppBar fixo no topo com z-index elevado
 */
export const AppBar = muiStyled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<MenuStyleProps>(({ theme, open, isMobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: '#f3f3f3',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  height: HEADER_HEIGHT,
  position: 'fixed',
  width: '100%',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}));

/**
 * Drawer lateral com variantes para desktop e mobile
 */
export const DrawerStyled = muiStyled(Drawer, {
  shouldForwardProp: (prop) => !['open', 'isMobile'].includes(prop as string),
})<MenuStyleProps>(({ theme, open, isMobile }) => ({
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: isMobile ? DRAWER_WIDTH : (open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH),
    backgroundColor: '#f3f3f3',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    // No desktop, ajustamos o topo para não sobrepor o AppBar
    ...(isMobile ? {
      top: 0,
      height: '100%',
      zIndex: theme.zIndex.drawer + 2,
      position: 'fixed',
    } : {
      top: `${HEADER_HEIGHT}px`,
      height: `calc(100% - ${HEADER_HEIGHT}px)`,
    }),
    transition: theme.transitions.create(['width', 'transform'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

/**
 * Container principal para o conteúdo
 */
export const MainContainer = muiStyled(Box, {
  shouldForwardProp: (prop) => !['open', 'isMobile'].includes(prop as string),
})<MenuStyleProps>(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  marginLeft: isMobile ? 0 : (open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH),
  marginTop: HEADER_HEIGHT, // Adiciona margem para não sobrepor o AppBar
  width: isMobile ? '100%' : `calc(100% - ${open ? DRAWER_WIDTH : COLLAPSED_DRAWER_WIDTH}px)`,
  backgroundColor: '#f5f5f7', // Fundo cinza claro para destacar os componentes
  minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`, // Garante altura mínima para preencher a tela
  height: '100%', // Altura completa
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

/**
 * Toolbar estilizada para o header
 */
export const ToolbarStyled = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
  height: ${HEADER_HEIGHT}px;
  padding: 0 16px;
`;

/**
 * Header do Drawer
 */
export const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  min-height: 64px;
`;

/**
 * Container para o logo no menu lateral
 */
export const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
`;

/**
 * Container para item do menu
 */
export const MenuItemContainer = styled(ListItem)<IListMenu>`
  padding: 0px;
  max-width: ${DRAWER_WIDTH}px;
  border-radius: 10px;
  margin-bottom: 4px;
  padding: 0px;
  position: relative;
  
  ${props => props.ativo && props.open && `
    background-color: rgba(25, 118, 210, 0.08);
    border-left: 4px solid #1976d2;
  `}
  
  ${props => props.ativo && !props.open && `
    background-color: transparent;
  `}
  
  ${props => !props.open && `
    & .MuiListItemButton-root {
      background-color: transparent;
    }
  `}
`;

/**
 * Container para o campo de busca
 */
export const SearchContainer = styled.div`
  min-width: 180px;
  justify-content: flex-end;
`;

/**
 * Container para o conteúdo da página
 */
export const ContentContainer = styled(Grid)`
  padding: 24px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`;

/**
 * GridItem para layouts responsivos
 */
export const GridItem = styled(Grid)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  align-content: center;
  background-color: #ffffff;
  @media (min-width: 900px) {
    background-color: ${(props: any) => props.color};
  }
`;
